import { BaseApiClient } from '@/services/baseApiClient';
import type { RequestOptions } from '@/utils/request';
import { getQuery } from '@/components/filter/helpers';
import type { BillingPopulate } from '@enmon/commons/lib/enmon';
import type {
  CalculateBillingArgs,
  DeleteBillingArgs,
  GetBillingArgs,
  GetBillingIntegrityArgs,
  GetBillingsArgs,
  PreviewBillingArgs,
} from './types/args';
import type {
  CalculateBillingResult,
  DeleteBillingResult,
  GetBillingIntegrityResult,
  GetBillingResult,
  GetBillingsResult,
  PreviewBillingResult,
} from './types/results';

export class BillingsApiClient extends BaseApiClient {
  constructor() {
    super('api/billings');
  }

  public getOne(
    billingId: GetBillingArgs,
    populate: BillingPopulate[] = [],
    signal?: RequestOptions['signal'],
  ): Promise<GetBillingResult> {
    return this.http.get(billingId, { query: { populate: populate?.join(',') }, signal });
  }

  public get(
    filter: GetBillingsArgs,
    populate: BillingPopulate[] = [],
    signal?: RequestOptions['signal'],
  ): Promise<GetBillingsResult> {
    return this.http.get('', { query: { ...getQuery(filter), populate: populate?.join(',') }, signal });
  }

  public getOnePreview(
    billingId: GetBillingArgs,
    populate: BillingPopulate[] = [],
    signal?: RequestOptions['signal'],
  ): Promise<GetBillingResult> {
    return this.http.get(`${billingId}/preview`, { query: { populate: populate?.join(',') }, signal });
  }

  public getIntegrity(
    id: GetBillingIntegrityArgs,
    signal?: RequestOptions['signal'],
  ): Promise<GetBillingIntegrityResult> {
    return this.http.get(`${id}/integrity`, { signal });
  }

  public calculate({ id, force }: CalculateBillingArgs): Promise<CalculateBillingResult> {
    return this.http.put(`${id}/calculate`, { force });
  }

  public delete(id: DeleteBillingArgs): Promise<DeleteBillingResult> {
    return this.http.delete(`${id}`);
  }

  public preview(payload: PreviewBillingArgs, signal?: RequestOptions['signal']): Promise<PreviewBillingResult> {
    return this.http.post('preview', payload, { signal });
  }
}
